import { format } from 'date-fns';
import { FilePen, Globe, GlobeLock, Plus, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Badge } from '../components/Badge';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/forms/ConfirmDialog';
import {
  type ChangelogEntry,
  type CreateChangelogPayload,
  type UpdateChangelogPayload,
  useChangelogAdmin,
  useCreateChangelog,
  useDeleteChangelog,
  usePublishChangelog,
  useUnpublishChangelog,
  useUpdateChangelog,
} from '../hooks/useChangelogData';

const PAGE_SIZE = 50;

function statusBadgeVariant(status: string): 'success' | 'warning' {
  return status === 'PUBLISHED' ? 'success' : 'warning';
}

interface EntryFormProps {
  initial?: ChangelogEntry | null;
  onSaveDraft: (payload: CreateChangelogPayload | UpdateChangelogPayload) => void;
  onSavePublish: (payload: CreateChangelogPayload | UpdateChangelogPayload) => void;
  isPending: boolean;
  onClose: () => void;
}

function EntryForm({ initial, onSaveDraft, onSavePublish, isPending, onClose }: EntryFormProps) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [body, setBody] = useState(initial?.body ?? '');
  const [tagsRaw, setTagsRaw] = useState((initial?.tags ?? []).join(', '));
  const [version, setVersion] = useState(initial?.version ?? '');

  function buildPayload(): CreateChangelogPayload {
    const isEdit = initial != null;
    return {
      title,
      body,
      tags: tagsRaw
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      // On create: omit version when empty. On edit: always include so a cleared value persists.
      ...(isEdit ? { version } : version ? { version } : {}),
    };
  }

  function handleDraft(e: FormEvent) {
    e.preventDefault();
    onSaveDraft(buildPayload());
  }

  function handlePublish(e: FormEvent) {
    e.preventDefault();
    onSavePublish({ ...buildPayload(), status: 'PUBLISHED' });
  }

  return (
    <form className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1">Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-text-secondary mb-1">
          Body (Markdown)
        </label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors resize-y font-mono"
        />
      </div>
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="block text-sm font-medium text-text-secondary mb-1">
            Tags (comma-separated)
          </label>
          <input
            type="text"
            value={tagsRaw}
            onChange={(e) => setTagsRaw(e.target.value)}
            placeholder="ui, analytics, ai"
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          />
        </div>
        <div className="w-36">
          <label className="block text-sm font-medium text-text-secondary mb-1">Version</label>
          <input
            type="text"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder="2.1.0"
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleDraft}
          disabled={isPending || !title}
          className="px-4 py-2 text-sm rounded-lg border border-border font-medium text-text-secondary hover:bg-surface-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isPending ? 'Saving...' : 'Save as draft'}
        </button>
        <button
          type="button"
          onClick={handlePublish}
          disabled={isPending || !title}
          className="px-4 py-2 text-sm rounded-lg font-medium bg-primary text-background hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isPending ? 'Saving...' : 'Save & publish'}
        </button>
      </div>
    </form>
  );
}

export function Changelog() {
  const [page, setPage] = useState(1);
  const offset = (page - 1) * PAGE_SIZE;

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ChangelogEntry | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ChangelogEntry | null>(null);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const { data, isLoading, isError } = useChangelogAdmin({ limit: PAGE_SIZE, offset });
  const createMutation = useCreateChangelog();
  const updateMutation = useUpdateChangelog();
  const publishMutation = usePublishChangelog();
  const unpublishMutation = useUnpublishChangelog();
  const deleteMutation = useDeleteChangelog();

  const entries = data?.data ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  function showNotification(type: 'success' | 'error', message: string) {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 3000);
  }

  function handleCreate(payload: CreateChangelogPayload, publish: boolean) {
    createMutation.mutate(
      publish ? { ...payload, status: 'PUBLISHED' } : payload,
      {
        onSuccess: () => {
          setCreateOpen(false);
          showNotification('success', publish ? 'Entry published' : 'Draft saved');
        },
        onError: () => showNotification('error', 'Failed to save entry'),
      },
    );
  }

  function handleUpdate(payload: UpdateChangelogPayload, publish: boolean) {
    if (!editTarget) return;
    const id = editTarget.id;
    if (publish) {
      // Strip status from the PATCH body — the dedicated publish endpoint owns status transitions.
      const { status: _status, ...contentPayload } = payload;
      updateMutation.mutate(
        { id, data: contentPayload },
        {
          onSuccess: () => {
            publishMutation.mutate(id, {
              onSuccess: () => {
                setEditTarget(null);
                showNotification('success', 'Entry published');
              },
              onError: () => showNotification('error', 'Failed to publish entry'),
            });
          },
          onError: () => showNotification('error', 'Failed to update entry'),
        },
      );
    } else {
      updateMutation.mutate(
        { id, data: payload },
        {
          onSuccess: () => {
            setEditTarget(null);
            showNotification('success', 'Changes saved');
          },
          onError: () => showNotification('error', 'Failed to update entry'),
        },
      );
    }
  }

  function handlePublish(entry: ChangelogEntry) {
    publishMutation.mutate(entry.id, {
      onSuccess: () => showNotification('success', 'Entry published'),
      onError: () => showNotification('error', 'Failed to publish'),
    });
  }

  function handleUnpublish(entry: ChangelogEntry) {
    unpublishMutation.mutate(entry.id, {
      onSuccess: () => showNotification('success', 'Entry unpublished'),
      onError: () => showNotification('error', 'Failed to unpublish'),
    });
  }

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        showNotification('success', 'Entry deleted');
      },
      onError: () => showNotification('error', 'Failed to delete entry'),
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Changelog</h1>
          <p className="text-sm text-text-muted mt-0.5">
            Manage public product changelog entries visible to all customers.
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-background hover:bg-primary-hover transition-colors"
        >
          <Plus size={16} />
          New entry
        </button>
      </div>

      {isLoading ? (
        <div className="bg-surface rounded-xl border border-border p-4 animate-pulse space-y-2">
          <div className="h-4 bg-border rounded w-full" />
          <div className="h-4 bg-border rounded w-3/4" />
          <div className="h-4 bg-border rounded w-5/6" />
        </div>
      ) : isError ? (
        <div className="bg-surface rounded-xl border border-border px-5 py-8 text-center text-sm">
          <p className="text-danger font-medium">Failed to load changelog entries</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-3 text-text-secondary underline hover:text-text transition-colors text-xs"
          >
            Retry
          </button>
        </div>
      ) : entries.length === 0 ? (
        <div className="bg-surface rounded-xl border border-border px-5 py-8 text-center text-text-muted text-sm">
          No changelog entries found
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-text-muted">
                  <th className="px-5 py-2.5 font-medium text-xs">Title</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Status</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Version</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Published</th>
                  <th className="px-5 py-2.5 font-medium text-xs">Created</th>
                  <th className="px-5 py-2.5 font-medium text-xs" />
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={entry.id}
                    className="border-b border-border last:border-b-0 hover:bg-surface-hover transition-colors"
                  >
                    <td className="px-5 py-2.5">
                      <span className="text-text font-medium">{entry.title}</span>
                    </td>
                    <td className="px-5 py-2.5">
                      <Badge variant={statusBadgeVariant(entry.status)}>{entry.status}</Badge>
                    </td>
                    <td className="px-5 py-2.5">
                      {entry.version ? (
                        <span className="text-text-secondary">{entry.version}</span>
                      ) : (
                        <span className="text-text-muted">-</span>
                      )}
                    </td>
                    <td className="px-5 py-2.5">
                      <span className="text-text-secondary">
                        {entry.publishedAt
                          ? format(new Date(entry.publishedAt), 'MMM d, yyyy')
                          : '-'}
                      </span>
                    </td>
                    <td className="px-5 py-2.5">
                      <span className="text-text-secondary">
                        {format(new Date(entry.createdAt), 'MMM d, yyyy')}
                      </span>
                    </td>
                    <td className="px-5 py-2.5">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          type="button"
                          onClick={() => setEditTarget(entry)}
                          className="p-1.5 rounded-md text-text-muted hover:text-text hover:bg-surface-hover transition-colors"
                          title="Edit"
                          aria-label="Edit"
                        >
                          <FilePen size={15} />
                        </button>
                        {entry.status === 'DRAFT' ? (
                          <button
                            type="button"
                            onClick={() => handlePublish(entry)}
                            disabled={publishMutation.isPending}
                            className="p-1.5 rounded-md text-text-muted hover:text-success hover:bg-success/10 transition-colors disabled:opacity-40"
                            title="Publish"
                            aria-label="Publish"
                          >
                            <Globe size={15} />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleUnpublish(entry)}
                            disabled={unpublishMutation.isPending}
                            className="p-1.5 rounded-md text-text-muted hover:text-warning hover:bg-warning/10 transition-colors disabled:opacity-40"
                            title="Unpublish"
                            aria-label="Unpublish"
                          >
                            <GlobeLock size={15} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(entry)}
                          className="p-1.5 rounded-md text-text-muted hover:text-danger hover:bg-danger/10 transition-colors"
                          title="Delete"
                          aria-label="Delete"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-5 py-3 text-sm text-text-secondary border-t border-border">
            <span>
              {(data?.total ?? 0) > 0
                ? `${offset + 1}-${Math.min(offset + PAGE_SIZE, data?.total ?? 0)} of ${data?.total}`
                : '0 results'}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
                className="px-3 py-1 rounded-md hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Prev
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1 rounded-md hover:bg-surface-hover disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create changelog entry"
        size="lg"
      >
        <EntryForm
          onSaveDraft={(p) => handleCreate(p as CreateChangelogPayload, false)}
          onSavePublish={(p) => handleCreate(p as CreateChangelogPayload, true)}
          isPending={createMutation.isPending}
          onClose={() => setCreateOpen(false)}
        />
      </Modal>

      {editTarget && (
        <Modal
          open
          onClose={() => setEditTarget(null)}
          title="Edit changelog entry"
          size="lg"
        >
          <EntryForm
            initial={editTarget}
            onSaveDraft={(p) => handleUpdate(p, false)}
            onSavePublish={(p) => handleUpdate(p, true)}
            isPending={updateMutation.isPending || publishMutation.isPending}
            onClose={() => setEditTarget(null)}
          />
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmDialog
          open
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Confirm delete"
          message={`Permanently delete "${deleteTarget.title}"? This cannot be undone.`}
          confirmLabel="Delete"
          confirmVariant="danger"
          loading={deleteMutation.isPending}
        />
      )}

      {notification && (
        <div
          className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg border text-sm shadow-lg z-50 ${
            notification.type === 'success'
              ? 'bg-success/15 border-success/30 text-success'
              : 'bg-danger/15 border-danger/30 text-danger'
          }`}
        >
          {notification.message}
        </div>
      )}
    </div>
  );
}
